import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PrintIcon from '@mui/icons-material/Print';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import API from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { generateBusinessHeader, openPrintWindow } from '../../utils/printUtils';

const HEBREW_MONTHS = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];


function formatCurrencyIL(num) {
  return parseFloat(num || 0).toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
}

function formatDateIL(isoStr) {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('-');
  return `${d}.${m}.${y}`;
}

function toLocalISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function generatePrintHTML(data, uiConfig, periodLabel, businessProfile) {
  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {};
  const orderLabel = generalSchema?.order?.label || 'הזמנה';
  const orderPluralLabel = generalSchema?.order?.plural_label || 'הזמנות';
  const productLabel = generalSchema?.product?.label || 'מוצר';

  const generatedDate = new Date().toLocaleDateString('he-IL');

  const orderBlocks = data.orders.map(order => {
    const orderDate = formatDateIL(order.OrderDate);
    const itemRows = order.items.map(item => `
      <tr>
        <td>${item.ProductName}</td>
        <td>${parseFloat(item.Quantity)}</td>
        <td dir="ltr">${formatCurrencyIL(item.UnitPrice)}</td>
        <td dir="ltr">${formatCurrencyIL(item.Subtotal)}</td>
      </tr>
    `).join('');

    return `
      <table style="width:100%;border:none;border-collapse:collapse;margin:16px 0 0;border-bottom:2px solid #bbb;">
        <tr style="background:none;">
          <td style="border:none;padding:0 0 4px 0;text-align:right;font-size:18px;font-weight:bold;color:#333;">${orderLabel}</td>
          <td style="border:none;padding:0 0 4px 0;text-align:left;font-size:15px;color:#555;direction:ltr;unicode-bidi:embed;">#${order.OrderNumber} &bull; ${orderDate}</td>
        </tr>
      </table>
      <table>
        <thead><tr>
          <th>${productLabel}</th><th>כמות</th><th>מחיר יחידה</th><th>סה"כ</th>
        </tr></thead>
        <tbody>${itemRows || `<tr><td colspan="4" style="text-align:center;color:#888">אין פריטים</td></tr>`}</tbody>
        <tfoot><tr style="font-weight:bold;background:#f0f0f0">
          <td colspan="3">סה"כ ${orderLabel}</td>
          <td dir="ltr">${formatCurrencyIL(order.TotalAmount)}</td>
        </tr></tfoot>
      </table>
    `;
  }).join('');

  const noOrdersBlock = `<p style="text-align:center;color:#888;padding:16px">אין ${orderPluralLabel} בתקופה זו</p>`;

  return `<!DOCTYPE html>
<html dir="rtl">
<head>
  <meta charset="utf-8">
  <title>דוח ${orderPluralLabel} חודשי - ${data.client.ClientName} - ${periodLabel}</title>
  <style>
    @media print {
      @page { margin: 10mm; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      h3 { page-break-before: auto; }
    }
    body {
      direction: rtl;
      font-family: Arial, sans-serif;
      font-size: 16px;
      margin: 0;
      padding: 16px;
    }
    h1 { font-size: 26px; margin: 0 0 6px; text-align: center; }
    h2 { font-size: 22px; margin: 20px 0 8px; border-bottom: 2px solid #333; padding-bottom: 4px; }
    h3 { font-size: 18px; margin: 16px 0 6px; color: #333; }
    .meta { text-align: center; color: #555; font-size: 14px; margin-bottom: 20px; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 12px; }
    th, td { border: 1px solid #ccc; padding: 8px; text-align: center; vertical-align: middle; word-break: break-word; }
    th { background-color: #f0f0f0; font-weight: bold; }
    .summary-table { width: auto; min-width: 300px; margin: 0 auto; }
    .summary-table td { min-width: 140px; }
    .balance-box {
      display: inline-block;
      border: 2px solid #333;
      border-radius: 6px;
      padding: 8px 20px;
      font-size: 18px;
      font-weight: bold;
      margin: 8px 4px;
    }
    .center { text-align: center; }
  </style>
</head>
<body>
  ${generateBusinessHeader(businessProfile)}
  <h1>דוח ${orderPluralLabel} חודשי</h1>
  <div style="text-align:center; margin-bottom: 4px;">
    <span style="font-size:22px; font-weight:bold;">${data.client.ClientName}</span>
  </div>
  <div class="meta">
    ${data.client.ClientBusinessID}
    &nbsp;|&nbsp; תקופה: ${periodLabel}
    &nbsp;|&nbsp; הופק: ${generatedDate}
  </div>

  ${data.orders.length ? `
    <h2>${orderPluralLabel}</h2>
    ${orderBlocks}

    <h2>סיכום</h2>
    <table class="summary-table">
      <tbody>
        <tr>
          <td>מספר ${orderPluralLabel}</td>
          <td><strong>${data.order_summary.total_orders}</strong></td>
        </tr>
        <tr style="font-size:17px;font-weight:bold">
          <td>סה"כ ${orderPluralLabel}</td>
          <td dir="ltr" style="color:#c62828">${formatCurrencyIL(data.order_summary.total_order_value)}</td>
        </tr>
      </tbody>
    </table>
  ` : noOrdersBlock}
</body>
</html>`;
}

// Remembers whether the user last opened or closed an order in the statement,
// so orders show in that same state the next time the dialog is opened.
const ORDERS_EXPANDED_PREF_KEY = 'ordersMonthlyStatement.ordersExpanded';

const readOrdersExpandedPref = () => {
  try {
    const value = localStorage.getItem(ORDERS_EXPANDED_PREF_KEY);
    return value === null ? null : value === 'true';
  } catch {
    return null;
  }
};

const writeOrdersExpandedPref = (expanded) => {
  try {
    localStorage.setItem(ORDERS_EXPANDED_PREF_KEY, String(expanded));
  } catch {
    // Storage unavailable (private mode / blocked) — preference just isn't kept.
  }
};

const OrdersMonthlyStatementDialog = ({ open, onClose, clientBusinessId, clientName, uiConfig }) => {
  const { userProfile } = useUser();
  const businessProfile = userProfile?.group?.business_profile || {};
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [statementData, setStatementData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [error, setError] = useState('');
  const [rangeMode, setRangeMode] = useState('month');
  const [rangeStart, setRangeStart] = useState(new Date());
  const [rangeEnd, setRangeEnd] = useState(new Date());
  const [ordersExpandedPref, setOrdersExpandedPref] = useState(readOrdersExpandedPref);

  const generalSchema = uiConfig?.orders_general_configs?.general_schema || {};
  const orderLabel = generalSchema?.order?.label || 'הזמנה';
  const orderPluralLabel = generalSchema?.order?.plural_label || 'הזמנות';
  const productLabel = generalSchema?.product?.label || 'מוצר';

  useEffect(() => {
    if (open) {
      setStatementData(null);
      setError('');
      setSelectedDate(new Date());
      setRangeMode('month');
      setRangeStart(new Date());
      setRangeEnd(new Date());
      setOrdersExpandedPref(readOrdersExpandedPref());
    }
  }, [open]);

  const handleOrderExpandedChange = (_event, expanded) => {
    writeOrdersExpandedPref(expanded);
  };

  useEffect(() => {
    if (!open) return;
    if (rangeMode === 'month' && !selectedDate) return;
    if (rangeMode === 'range' && (!rangeStart || !rangeEnd || rangeStart > rangeEnd)) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setStatementData(null);
    const params = rangeMode === 'month'
      ? { business_id: clientBusinessId, year: selectedDate.getFullYear(), month: selectedDate.getMonth() + 1 }
      : { business_id: clientBusinessId, start_date: toLocalISODate(rangeStart), end_date: toLocalISODate(rangeEnd) };
    API.get('/client/orders-monthly-summary/', { params })
      .then(res => { if (!cancelled) setStatementData(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת הדוח'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, selectedDate, clientBusinessId, rangeMode, rangeStart, rangeEnd]);

  const handlePrint = () => {
    if (!statementData) return;
    setPrintLoading(true);
    const html = generatePrintHTML(statementData, uiConfig, periodLabel, businessProfile);
    openPrintWindow(html);
    setPrintLoading(false);
  };

  const handleClose = () => {
    if (!loading && !printLoading) onClose();
  };

  const periodLabel = rangeMode === 'month'
    ? (selectedDate ? `${HEBREW_MONTHS[selectedDate.getMonth()]} ${selectedDate.getFullYear()}` : '')
    : (rangeStart && rangeEnd ? `${formatDateIL(toLocalISODate(rangeStart))} - ${formatDateIL(toLocalISODate(rangeEnd))}` : '');

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <MobileDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
            <CalendarMonthIcon sx={{ color: 'secondary.main' }} />
            דוח {orderPluralLabel} חודשי
          </Box>
          <IconButton onClick={handleClose} disabled={loading || printLoading} sx={{ color: 'grey.500' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, overflowY: 'auto' }}>
          {/* Client info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">לקוח</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{clientName}</Typography>
          </Box>

          {/* Controls */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <RadioGroup
              row
              value={rangeMode}
              onChange={(e) => setRangeMode(e.target.value)}
            >
              <FormControlLabel value="month" control={<Radio size="small" />} label="חודש" />
              <FormControlLabel value="range" control={<Radio size="small" />} label="טווח תאריכים" />
            </RadioGroup>
            {rangeMode === 'month' ? (
              <DatePicker
                label="בחר חודש"
                value={selectedDate}
                onChange={(val) => setSelectedDate(val)}
                views={['year', 'month']}
                openTo="month"
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { '& .MuiOutlinedInput-root': { direction: 'ltr' } },
                  }
                }}
              />
            ) : (
              <>
                <DatePicker
                  label="מתאריך"
                  value={rangeStart}
                  onChange={(val) => setRangeStart(val)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { '& .MuiOutlinedInput-root': { direction: 'ltr' } },
                    }
                  }}
                />
                <DatePicker
                  label="עד תאריך"
                  value={rangeEnd}
                  onChange={(val) => setRangeEnd(val)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { '& .MuiOutlinedInput-root': { direction: 'ltr' } },
                    }
                  }}
                />
              </>
            )}
            {loading && <CircularProgress size={24} color="secondary" />}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
          )}

          {/* Statement Preview */}
          {statementData && (
            <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 1, overflow: 'hidden' }}>
              {/* Header */}
              <Box sx={{ bgcolor: 'secondary.main', color: 'white', p: 2, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  דוח {orderPluralLabel} — {periodLabel}
                </Typography>
                <Typography variant="body2">{clientName} ({clientBusinessId})</Typography>
              </Box>

              <Box sx={{ p: 2 }}>
                {statementData.orders.length === 0 ? (
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    אין {orderPluralLabel} בתקופה זו
                  </Typography>
                ) : (
                  <>
                    {/* Orders (accordion per order) */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {orderPluralLabel} ({statementData.order_summary.total_orders})
                    </Typography>

                    {statementData.orders.map((order, idx) => (
                      <Accordion
                        key={order.OrderNumber || idx}
                        defaultExpanded={ordersExpandedPref ?? statementData.orders.length <= 3}
                        onChange={handleOrderExpandedChange}
                        sx={{ mb: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {orderLabel} <span dir="ltr">#{order.OrderNumber}</span>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" dir="ltr">
                              {formatDateIL(order.OrderDate)}
                            </Typography>
                            <Box sx={{ mr: 'auto', display: 'flex', gap: 1 }}>
                              <Chip
                                label={`סה"כ: ${formatCurrencyIL(order.TotalAmount)}`}
                                size="small"
                                sx={{ bgcolor: '#ffebee' }}
                              />
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{productLabel}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>כמות</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>מחיר יחידה</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>סה"כ</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {order.items.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>
                                    אין פריטים
                                  </TableCell>
                                </TableRow>
                              ) : (
                                order.items.map((item, i) => (
                                  <TableRow key={i}>
                                    <TableCell align="center">{item.ProductName}</TableCell>
                                    <TableCell align="center">{parseFloat(item.Quantity)}</TableCell>
                                    <TableCell align="center" dir="ltr">{formatCurrencyIL(item.UnitPrice)}</TableCell>
                                    <TableCell align="center" dir="ltr">{formatCurrencyIL(item.Subtotal)}</TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </AccordionDetails>
                      </Accordion>
                    ))}

                    {/* Summary */}
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                      <Chip
                        label={`${statementData.order_summary.total_orders} ${orderPluralLabel}`}
                        sx={{ border: '1px solid #bdbdbd', fontWeight: 'bold' }}
                      />
                      <Chip
                        label={`סה"כ: ${formatCurrencyIL(statementData.order_summary.total_order_value)}`}
                        sx={{ bgcolor: '#ffebee', border: '1px solid #ef9a9a', fontWeight: 'bold' }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} disabled={loading || printLoading} size="large">
            סגור
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handlePrint}
            disabled={!statementData || printLoading || loading}
            startIcon={printLoading ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
            size="medium"
            sx={{ direction: 'ltr' }}
          >
            {printLoading ? 'מכין...' : 'הדפסה'}
          </Button>
        </DialogActions>
      </MobileDialog>
    </LocalizationProvider>
  );
};

export default OrdersMonthlyStatementDialog;
